'use client'
import React, { useState, useEffect, useMemo, useContext } from 'react'
import { Badge, Breadcrumbs, BreadcrumbItem } from "@heroui/react"

import localStorage from '../src/local-storage'
import { UserContext } from '../contexts/user-context'
import useFeedUnread from '../hooks/use-feed-unread'

import { menuContainerClass } from '../src/styles'

const menuItemClasses = {
  separator: 'pl pr md:pl-2 md:pr-2',
  item: 'data-[current=false]:text-primary hover:text-primary data-[current=true]:text-primary data-[current=true]:font-bold'
}

const donateChallenges = [
  '88ecb460-bc00-11ee-a03f-fbaef5503c01',
  '3d479520-98d9-11ed-9569-25a24b126a8d',
  'cf7fa7a0-c8f0-11ee-86a3-c1d88febeda3',
  '0775f5a0-d8eb-11ef-b852-83e26af986de'
]

export const ChallengeOptions = ({ page, challenge }) => {
  const userHook = useContext(UserContext)
  const user = userHook.user
  
  const [isAdmin, setIsAdmin] = useState(false)
  const [hidden, setHidden] = useState(false)
  const badgeHook = useFeedUnread(user.token, challenge.id)
  
  useEffect(() => {
    const adminUser = localStorage.getAdminUser()
    if (adminUser && adminUser.id) setIsAdmin(true)

    // this hides options from JOIN page and challenges that people have not joined
    if (challenge && challenge.id && challenge.group.organizationId !== user.organizationId) {
      if (!user.challengesJoined) return setHidden(true)
      if (!user.challengesJoined.map(c => c.id).includes(challenge.id)) return setHidden(true)
    }
  }, [])

  if (hidden) return <></>
  return <div className={menuContainerClass}>
    <Breadcrumbs
      size="lg"
      separator={'|'}
      disableAnimation={true}
      itemClasses={menuItemClasses}>
        <BreadcrumbItem href={`/challenge/${challenge.id}`} isCurrent={page === 'challenge'}>dashboard</BreadcrumbItem>
        <BreadcrumbItem href={`/challenge/${challenge.id}/activities`} isCurrent={page === 'activities'}>activities</BreadcrumbItem>
        {donateChallenges.includes(challenge.id) && 
          <BreadcrumbItem href={`/challenge/${challenge.id}/donate`} isCurrent={page === 'donate'}>donate</BreadcrumbItem>
        }
        <BreadcrumbItem href={`/challenge/${challenge.id}/feed`} isCurrent={page === 'feed'}>
          {(!badgeHook.loading && badgeHook.data.length) ? <Badge 
            classNames={{ base: 'absolute text-secondary hover:text-primary'}} 
            content={badgeHook.data.length} 
            size="md" 
            color="warning" 
            showOutline={false} >feed&nbsp;&nbsp;&nbsp;</Badge> : 'feed'}
        </BreadcrumbItem>
        {(user && challenge.group.organizationId !== user.organizationId && challenge.group.teamClone && !challenge.over) && 
          <BreadcrumbItem href={`/challenge/${challenge.id}/invite`} isCurrent={page === 'invite'}>invite</BreadcrumbItem>
        }
    </Breadcrumbs>
    
    {(user && challenge.group.organizationId === user.organizationId) && 
      <div className='flex items-center'>
        <div className='text-sm md:text-base font-bold mr-2 text-chSubtext'>Admin:</div>
        <div className='grow'>
          <Breadcrumbs
            size="lg"
            disableAnimation={true}
            separator={'|'}
            itemClasses={menuItemClasses}>
              <BreadcrumbItem href={`/challenge/${challenge.id}/edit`} isCurrent={page === 'edit'} isDisabled={challenge.over}>edit</BreadcrumbItem>
              <BreadcrumbItem href={`/challenge/${challenge.id}/participants`} isCurrent={page === 'participants'}>participants</BreadcrumbItem>
              <BreadcrumbItem href={`/join/${challenge.id}`} isCurrent={page === 'join'} isDisabled={challenge.over}>join</BreadcrumbItem>
              <BreadcrumbItem href={`/challenge/${challenge.id}/invite`} isCurrent={page === 'invite'} isDisabled={challenge.over}>invite</BreadcrumbItem>
              <BreadcrumbItem href={`/challenge/new/edit?cloneId=${challenge.id}`} isCurrent={page === 'clone'}>clone</BreadcrumbItem>
              {isAdmin &&
                <BreadcrumbItem isCurrent={page === 'map'} href={`/challenge/${challenge.id}/map-edit`}>map</BreadcrumbItem>
              }
          </Breadcrumbs>
        </div>
      </div>
    }
   </div>
}

export const GroupOptions = ({ page, user, group }) => {
  const [showAdmin, setShowAdmin] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  
  useMemo(() => {
    if (Boolean(typeof window === 'undefined')) return // server
    if (user && user.organizationId && group.organizationId === user.organizationId) setShowAdmin(true)
    const adminUser = localStorage.getAdminUser()
    if (adminUser && adminUser.id) setIsAdmin(true)
  }, [user, group])

  if (!group) return <></>

  let activityParams = '' 
  if (group.type === 'team') activityParams = '?aggregates=true&orderBy=distance'

  return <div className={menuContainerClass}>
    <Breadcrumbs
      size="lg"
      separator={'|'}
      disableAnimation={true}
      itemClasses={menuItemClasses}>
        <BreadcrumbItem href={`/group/${group.id}`} isCurrent={page === 'challenges'}>{group.type}s</BreadcrumbItem>
        <BreadcrumbItem href={`/group/${group.id}/activities${activityParams}`} isCurrent={page === 'activities'}>activities</BreadcrumbItem>
        <BreadcrumbItem href={`/group/${group.id}/reports`} isCurrent={page === 'reports'}>results</BreadcrumbItem>
    </Breadcrumbs>
    
    {showAdmin && 
      <div className='flex items-center'>
        <div className='text-sm md:text-base font-bold mr-2 text-gray-500'>Admin:</div>
        <div className='grow'>
          <Breadcrumbs
            size="lg"
            disableAnimation={true}
            separator={'|'}
            itemClasses={menuItemClasses}>
              <BreadcrumbItem href={`/group/${group.id}/edit`} isCurrent={page === 'edit'}>edit group</BreadcrumbItem>
              <BreadcrumbItem href={`/challenge/new/edit?groupId=${group.id}`} isCurrent={page === 'add'}>add {group.type}</BreadcrumbItem>
              {isAdmin &&
                <BreadcrumbItem isCurrent={page === 'map'} href={`/group/${group.id}/map-edit`}>map</BreadcrumbItem>
              }
          </Breadcrumbs>
        </div>
      </div>
    }
   </div>
}

export default ChallengeOptions
